import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { fetchCotaSecretario } from '@/api/secretario';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { classifyError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

const CASO_SO_COM_ASSUNTO =
  'Os casos novos chegam só com o nome e o assunto, sem documentos. Atualizações de processo seguem normais.';
const SEM_PLANO =
  'Os casos novos chegam só com o nome e o assunto, sem documentos. Adicione o Secretário à sua assinatura para o atendimento completo.';

/**
 * Os atendimentos novos do mês no Secretário (docs/PLANO_SECRETARIO_ADDON.md).
 *
 * Faixa só quando pede atenção: a partir de 80%, com a cota esgotada e quando o
 * Secretário não está no plano. No uso normal, nada aqui — a contagem é o
 * ``CotaSecretarioContador``, na linha das abas. A regra (o que conta, as faixas) é
 * do backend: a tela só pinta o que ``GET /secretario/configuracao/cota`` devolve.
 */
export function CotaSecretarioFaixa() {
  const { data: cota, error } = useCota();

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{classifyError(error).message}</AlertDescription>
      </Alert>
    );
  }
  if (!cota) return null;

  const usados = `${cota.atendimentos_usados} de ${cota.atendimentos_max ?? '—'}`;
  const assinatura = (
    <Button asChild variant="outline">
      <Link to="/app/assinatura">Ver assinatura</Link>
    </Button>
  );

  if (cota.faixa === 'SEM_DIREITO') {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>O Secretário de IA não está no seu plano</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
          <span>{SEM_PLANO}</span>
          {assinatura}
        </AlertDescription>
      </Alert>
    );
  }
  if (cota.faixa === 'ESGOTADA') {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>As {cota.atendimentos_max} triagens deste mês acabaram</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
          <span>{CASO_SO_COM_ASSUNTO} A contagem recomeça no dia 1º.</span>
          {cota.origem === 'PLANO' && assinatura}
        </AlertDescription>
      </Alert>
    );
  }
  if (cota.motivo_atendimento_minimo === 'teto_interno') {
    // O teto interno (abuso: um número inundando o canal) não tem número na tela —
    // ele não é cota do advogado. Mas o efeito é o mesmo, e a tela não pode dizer
    // que está tudo normal enquanto os casos chegam sem documentos.
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>O Secretário atingiu o limite de uso deste mês</AlertTitle>
        <AlertDescription>{CASO_SO_COM_ASSUNTO}</AlertDescription>
      </Alert>
    );
  }
  if (cota.faixa === 'ATENCAO') {
    return (
      <Alert variant="warning">
        <MessageCircle className="h-4 w-4" />
        <AlertDescription>
          {usados} triagens usadas neste mês. Consultas de andamento não contam.
        </AlertDescription>
      </Alert>
    );
  }
  return null;
}

function useCota() {
  return useQuery({
    queryKey: queryKeys.secretario.cota,
    queryFn: fetchCotaSecretario,
    staleTime: 60 * 1000,
  });
}

/**
 * A contagem do mês no uso normal, compacta ao lado das abas (Adriano, 29/10: a linha
 * própria ocupava espaço demais); o detalhe fica no tooltip. Quando a cota pede
 * atenção, quem fala é a faixa, e o contador some.
 */
export function CotaSecretarioContador() {
  const { data: cota } = useCota();
  if (!cota || cota.atendimentos_max == null) return null;
  if (cota.faixa !== 'NORMAL' || cota.motivo_atendimento_minimo === 'teto_interno') return null;
  // Provider próprio, como o do editor: a página também é montada fora do App (testes).
  return (
    <TooltipProvider delayDuration={300}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="min-h-11 text-muted-foreground sm:min-h-9">
          <MessageCircle className="mr-1.5 h-4 w-4" />
          {cota.atendimentos_usados}/{cota.atendimentos_max} triagens
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        Triagens deste mês. Consultas de andamento não contam; a contagem recomeça no dia 1º.
      </TooltipContent>
    </Tooltip>
    </TooltipProvider>
  );
}
